import { useCallback } from 'react';

import type { ErrorLike } from '@apollo/client';
import {
  useFragment as useApolloFragment,
  useLazyQuery,
} from '@apollo/client/react';
import { Skeleton, Stack } from '@mui/material';
import { generatedTextToEditorHtml } from '@realadvisor/html-sanitizer/generated-text';

import { gql } from '@graphql/__generated__';
import { IMPROVE_TEXT } from '@modules/_shared/common/queries';
import { AiDescriptionMenu } from '@modules/_shared/components/AiDescriptionMenu';
import {
  type FormDefinitionType,
  RaForm,
} from '@modules/_shared/components/form/RaForm';
import {
  type ReportComponentProps,
  useReportContext,
} from '@modules/_shared/components/report-editor/ReportEditorContext';
import { STEP_DESCRIPTION_FRAGMENT } from '@modules/reports/cma-reports/cmaReportsQueries';
import {
  DescriptionLanguageTabs,
  LANGUAGE_API_NAMES,
} from '@modules/reports/shared/DescriptionLanguageTabs';
import { FooterActions } from '@modules/reports/shared/FooterActionsComponentWithoutForm';
import {
  useLanguageTranslations,
  useTranslationUpdate,
} from '@modules/reports/shared/language';
import { useUpdateCmaReport } from '@modules/reports/shared/utils';
import { type Language } from '@shared/LocaleContext';

const GENERATE_CMA_DESCRIPTION = gql(/* GraphQL */ `
  query GenerateCmaDescription($cma_id: uuid!, $language: String) {
    generate_cma_description(cma_id: $cma_id, language: $language) {
      description
      prompt
    }
  }
`);

type StepDescriptionForm = Record<string, never>;

const CmaGenerateDescription = ({
  cmaReportId,
  language,
  description,
  onComplete,
  onError,
  translateFrom,
  sourceLanguages,
  isTranslating,
}: {
  cmaReportId: string;
  language?: string;
  description?: string;
  onComplete: (description: string) => void;
  onError: (error: ErrorLike) => void;
  translateFrom?: (sourceLanguage: Language) => Promise<void>;
  sourceLanguages?: { code: Language; name: string }[];
  isTranslating?: boolean;
}) => {
  const [generateDesc, { loading: isGenerating }] = useLazyQuery(
    GENERATE_CMA_DESCRIPTION,
    {
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'no-cache',
      // The client-wide default is false, which in Apollo v4 suppresses the
      // loading=true emission useLazyQuery relies on.
      notifyOnNetworkStatusChange: true,
    },
  );

  const [improveDesc, { loading: loadingImprove }] = useLazyQuery(
    IMPROVE_TEXT,
    {
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
    },
  );

  return (
    <AiDescriptionMenu
      onGenerate={async () => {
        const { data, error } = await generateDesc({
          variables: {
            cma_id: cmaReportId,
            language,
          },
        });
        if (error) {
          onError(error);
          return;
        }
        if (data?.generate_cma_description?.description) {
          onComplete(data.generate_cma_description.description);
        }
      }}
      onImprove={
        description
          ? async () => {
              const { data, error } = await improveDesc({
                variables: { text: description },
              });
              if (error) {
                onError(error);
                return;
              }
              if (data?.improve_text?.text) {
                onComplete(data.improve_text.text);
              }
            }
          : undefined
      }
      isLoading={isGenerating || loadingImprove || isTranslating}
      hasDescription={!!description}
      translateFrom={translateFrom}
      sourceLanguages={sourceLanguages}
    />
  );
};

const StepDescription = (props: ReportComponentProps) => {
  const { reportId: cmaReportId } = props;
  const { descriptionLanguage, setError, setDescriptionLanguage } =
    useReportContext();

  const { data, complete } = useApolloFragment({
    fragment: STEP_DESCRIPTION_FRAGMENT,
    fragmentName: 'StepDescription',
    from: {
      __typename: 'cma_reports',
      id: cmaReportId,
    },
  });

  const [updateCmaReport, updating] = useUpdateCmaReport(
    cmaReportId,
    'page-description',
  );

  const { updateTranslation, debouncedTranslationUpdate } =
    useTranslationUpdate(updateCmaReport, 'description');

  const translations = useLanguageTranslations(data, 'description');

  const stepDescriptionFormDefinition: FormDefinitionType<StepDescriptionForm> =
    useCallback(
      () => [
        {
          name: 'description_language_tabs',
          type: 'custom',
          gridProps: { md: 12 },
          element: (
            <DescriptionLanguageTabs
              defaultLanguage={descriptionLanguage}
              translations={translations}
              onTranslationChange={debouncedTranslationUpdate}
              onTranslationComplete={updateTranslation}
              onTranslationDelete={lang =>
                updateCmaReport({ [`description_${lang}`]: null })
              }
              onDescriptionLanguageChange={setDescriptionLanguage}
              actions={(
                activeTab,
                setContent,
                translateFrom,
                sourceLanguages,
                isTranslating,
              ) => (
                <CmaGenerateDescription
                  cmaReportId={cmaReportId}
                  language={LANGUAGE_API_NAMES[activeTab]}
                  description={translations[activeTab] || undefined}
                  isTranslating={isTranslating}
                  onComplete={(text: string) => {
                    // The AI returns markdown; convert it to the editor's HTML
                    // (with empty <p> for blank lines) so paragraph spacing is
                    // preserved and the stored value matches the html format.
                    const html = generatedTextToEditorHtml(text);
                    updateTranslation(activeTab, html);
                    setContent(html);
                  }}
                  onError={setError}
                  translateFrom={translateFrom}
                  sourceLanguages={sourceLanguages.map(code => ({
                    code,
                    name: LANGUAGE_API_NAMES[code],
                  }))}
                />
              )}
            />
          ),
        },
      ],
      [
        descriptionLanguage,
        translations,
        debouncedTranslationUpdate,
        updateTranslation,
        updateCmaReport,
        setDescriptionLanguage,
        cmaReportId,
        setError,
      ],
    );

  const onSubmit = async () => {
    props.setStep(props.step + 1);
  };

  if (!complete) {
    return (
      <Stack
        sx={{
          justifyContent: 'start',
          p: 2,
        }}
      >
        <Skeleton height={240} sx={{ transform: 'none' }} />
      </Stack>
    );
  }

  return (
    <RaForm
      freezeInitialDefaultValues={true}
      formDefinition={stepDescriptionFormDefinition}
      defaultValues={{}}
      onSubmit={onSubmit}
      actionButtonsComponent={<FooterActions {...props} updating={updating} />}
      contentScrollable
    />
  );
};

export default StepDescription;
